import React, { useRef, useState } from 'react';
import { Button } from '../UI/Button/Button';
import clsx from 'clsx';
import useOnClickOutside from '@/hooks/useClickOutside';
import Icon from '../UI/Icon/Icon';
import Tooltip from '../UI/Tooltip/Tooltip';
import CheckboxInput from '../UI/Form/CheckboxInput/CheckboxInput';

type ColumnManagerSelector = {
  toggleColumnVisibility: (v: boolean | null) => void;
  isAllColumnsVisible: boolean;
  numberOfVisibleColumns: number;
};

const CustomColumnManagerSelector = ({
  toggleColumnVisibility,
  isAllColumnsVisible,
  numberOfVisibleColumns,
}: ColumnManagerSelector) => {
  const [isSelectorDropdownOpen, setIsSelectorDropdownOpen] = useState(false);
  const selectorDropdownRef = useRef(null);

  useOnClickOutside(selectorDropdownRef, () => setIsSelectorDropdownOpen(false));

  const handleSelectorDropdownClick = (value = true) => {
    toggleColumnVisibility(value);
    setIsSelectorDropdownOpen(false);
  };

  return (
    <div className='relative'>
      <div
        className={clsx(
          'flex items-center ml-[26px] rounded',
          isSelectorDropdownOpen ? 'bg-shadow-100' : ''
        )}
      >
        <Tooltip
          text={numberOfVisibleColumns === 0 ? 'Select All' : 'Unselect All'}
          side='bottom'
          className='py-0'
        >
          <div className='flex items-center px-2 h-8 rounded-l hover:bg-shadow-100'>
            {!!numberOfVisibleColumns && !isAllColumnsVisible && (
              <span className='absolute z-20 top-2 left-[34px] w-4 h-4 rounded bg-midnight-500 border-ui-300 after:content-["-"] text-white flex justify-center items-center pointer-events-none' />
            )}
            <CheckboxInput
              checked={isAllColumnsVisible}
              onChange={() => toggleColumnVisibility(null)}
              dataCy='column-manager-top-checkbox'
            />
          </div>
        </Tooltip>

        <Tooltip text={'Select'} side='bottom' className='py-0'>
          <button
            onClick={() => setIsSelectorDropdownOpen((prev) => !prev)}
            className='flex items-center px-2 h-8 rounded-r hover:bg-shadow-100'
          >
            <Icon name='caret-down' weight='fill' size={16} />
          </button>
        </Tooltip>
      </div>
      {isSelectorDropdownOpen && (
        <div
          className={clsx(
            'absolute z-10 right-0 rounded w-64 border border-ui p-2 bg-white left-10 top-9'
          )}
          ref={selectorDropdownRef}
          data-cy='selector-dropdown'
        >
          <ul>
            <li>
              <Button
                className='!w-full !justify-start !text-ui'
                onClick={() => handleSelectorDropdownClick(false)}
                variant='ghost'
                dataCy='selector-dropdown-item-all'
                color='midnight'
              >
                All
              </Button>
            </li>
            <li>
              <Button
                className='!w-full !justify-start !text-ui'
                onClick={() => handleSelectorDropdownClick(true)}
                variant='ghost'
                dataCy='selector-dropdown-item-none'
                color='midnight'
              >
                None
              </Button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomColumnManagerSelector;
